import React from 'react';

import {
  ASSETS_TYPES,
  LOANS_STATUS,
  LOANS_STATUS_NAME_MAP,
} from '../constants/constants';
import StatusBadge from '../modules/status-badge/status-badge';
import {
  ASSET_TOKEN_STATUS,
  ORDER_DETAILED_STATUS,
  ORDER_STATUS,
  STATUS_COLOR_TYPE,
  TRANSACTION_TYPE,
  USERS_ACTION_TYPE,
} from '../types';

import TokenStatusBadge, {
  TokenStatusBadgeVariant,
} from 'components/TokenStatusBadge';

const getOrderDetailedStatusBadge = (status: ORDER_DETAILED_STATUS) => {
  const details: { title: string; color?: STATUS_COLOR_TYPE } = { title: '' };
  switch (status) {
    case 'DELIVERED':
      details.title = 'Токен исполнен';
      details.color = 'gray';
      break;
    case 'UCP_ISSUING_REQUIRED':
      details.title = 'Требуется выпуск';
      break;
    case 'UCP_PAYMENT_REQUIRED':
      details.title = 'Ожидание согласования';
      details.color = 'blue-light';
      break;
    case 'ADJUSTMENT_AGREEMENT':
      details.title = 'Согласование';
      details.color = 'yellow';
      break;
    case 'ADJUSTMENT_REQUIRED':
      details.title = 'Корректировка';
      details.color = 'orange';
      break;
    case 'ADJUSTMENT_CORRECTED':
      details.title = 'Откорректировано';
      details.color = 'green';
      break;
    case 'ADJUSTMENT_REJECTED':
      details.title = 'Корректировка отклонена';
      details.color = 'red';
      break;
    case 'WAITING_FOR_SHIPMENT':
      details.title = 'Ожидание поставки';
      break;
    case 'WAITING_FOR_ISSUING':
      details.title = 'Ожидание выпуска';
      break;
    case 'REJECTED':
      details.title = 'Отказ';
      details.color = 'red';
      break;
    default:
      break;
  }

  return <StatusBadge title={details.title} color={details.color} />;
};

export const getSaleStatusBadge = (status: string, isInitiator: boolean) => {
  let text = '';
  let variant: TokenStatusBadgeVariant | undefined = undefined;
  let notDisplay = false;
  switch (status) {
    case 'DOC_SIGNED':
      text = isInitiator
        ? 'Требуется подписание Ключом'
        : 'Необходимо подтвердить заявку';
      variant = 'yellow';
      break;
    case 'SIGNED':
      text = isInitiator
        ? 'Отправлено на согласование Покупателю'
        : 'Требуется подтвердить оплату';
      variant = isInitiator ? 'grey' : 'yellow';
      break;
    case 'AGENT_DOC_SIGNED':
      text = isInitiator
        ? 'Ожидается подтверждение оплаты'
        : 'Ожидается подтверждение оплаты';
      variant = 'grey';
      break;
    case 'PAYMENT_DOC_UPLOADED':
      if (!isInitiator) notDisplay = true;
      text = 'Требуется подтвердить оплату';
      variant = 'yellow';
      break;
    case 'APPLIED':
      text = 'Завершено';
      variant = 'green';
      break;
  }
  return notDisplay ? null : <TokenStatusBadge text={text} variant={variant} />;
};

export const getPurchaseStatusBadge = (
  status: string,
  isInitiator: boolean
) => {
  let text = '';
  let variant: TokenStatusBadgeVariant | undefined = undefined;
  switch (status) {
    case 'INITIALIZED':
      text = isInitiator
        ? 'Требуется подписание Заявления'
        : 'Ожидается подписание Заявления';
      variant = 'yellow';
      break;
    case 'SIGNED':
      text = isInitiator
        ? 'Отправлено на согласование Продавцу'
        : 'Необходимо подтвердить заявку';
      variant = isInitiator ? 'grey' : 'yellow';
      break;
    case 'AGENT_DOC_SIGNED':
      text = isInitiator
        ? 'Ожидается подтверждение резервирования токенов'
        : 'Ожидается подтверждение резервирования токенов';
      variant = isInitiator ? 'yellow' : 'grey';
      break;
    case 'AGENT_SIGNED':
      text = isInitiator
        ? 'Требуется подтвердить оплату'
        : 'Ожидается подтверждение оплаты';
      variant = isInitiator ? 'yellow' : 'grey';
      break;
    case 'PAYMENT_DOC_UPLOADED':
      text = isInitiator
        ? 'Ожидается подтверждение оплаты'
        : 'Требуется подтвердить оплату';
      variant = isInitiator ? 'grey' : 'yellow';
      break;
    case 'APPLIED':
      text = 'Завершено';
      variant = 'green';
      break;
  }
  return <TokenStatusBadge text={text} variant={variant} />;
};

export const getExchangeStatusBadge = (
  status: string,
  isInitiator: boolean
) => {
  let text = '';
  let variant: TokenStatusBadgeVariant | undefined = undefined;
  let notDisplay = false;
  switch (status) {
    case 'INITIATOR_DOC_SIGNED':
      if (isInitiator) notDisplay = true;
      text = 'Требуется подписание Ключом';
      variant = 'yellow';
      break;
    case 'INITIATOR_SIGNED':
      text = isInitiator
        ? 'На согласовании у Адресата оферты'
        : 'Необходимо подтвердить заявку';
      variant = isInitiator ? 'grey' : 'yellow';
      break;
    case 'AGENT_DOC_SIGNED':
      text = isInitiator
        ? 'Ожидается подтверждение обмена'
        : 'Необходимо подтвердить обмен';
      variant = isInitiator ? 'grey' : 'yellow';
      break;
    case 'COMPLETED':
      text = 'Завершено';
      variant = 'green';
      break;
  }
  return notDisplay ? null : <TokenStatusBadge text={text} variant={variant} />;
};

const getOrderStatusBadge = (status: ORDER_STATUS) => {
  const details: { title: string; color?: STATUS_COLOR_TYPE } = { title: '' };
  switch (status) {
    case 'IN_PROGRESS':
      details.color = 'green';
      details.title = 'В работе';
      break;
    case 'NEW':
      details.title = 'Новый';
      break;
    case 'COMPLETED':
      details.color = 'gray';
      details.title = 'Исполнен';
      break;
    case 'ADJUSTMENT_AGREEMENT':
      details.color = 'yellow';
      details.title = 'Согласованние';
      break;
    case 'ADJUSTMENT_CORRECTED':
      details.color = 'gray';
      details.title = 'Откорректировано';
      break;
    case 'ADJUSTMENT_REJECTED':
      details.color = 'red-3';
      details.title = 'Корректировка отклонена';
      break;
    case 'ADJUSTMENT_REQUIRED':
      details.color = 'orange';
      details.title = 'Корректировка';
      break;
    default:
      break;
  }
  return <StatusBadge title={details.title} color={details.color} />;
};

const getAssetTokenStatusBadge = (
  status: ASSET_TOKEN_STATUS | LOANS_STATUS,
  type?: ASSETS_TYPES | 'SALE' | 'CREDIT'
) => {
  const details: { title: string; color?: STATUS_COLOR_TYPE } = { title: '' };
  switch (status) {
    case 'ACTIVE':
      details.title =
        type === ASSETS_TYPES.SIMPLE
          ? 'Выпущен'
          : 'Требуется поставка/Ожидает поставки';
      details.color = 'blue-light';
      break;
    case 'APPLIED':
      details.title =
        type === 'SALE' ? LOANS_STATUS_NAME_MAP[status] : 'Ожидает погашения';
      details.color = 'blue-light';
      break;
    case 'CREDIT_EXPIRY':
      details.title = 'Платёж просрочен';
      details.color = 'orange';
      break;
    case 'NOT_ISSUED':
      details.title = 'Не выпущен';
      details.color = 'gray';
      break;
    case 'REDEEMED':
      details.title = 'Погашен';
      details.color = 'gray-lite';
      break;
    // credit status
    case 'SIGNED':
      details.title = LOANS_STATUS_NAME_MAP[status];
      details.color = 'yellow';
      break;
    case 'CLOSED':
      details.title = LOANS_STATUS_NAME_MAP[status];
      details.color = 'gray';
      break;
    case 'DECLINED':
      details.title = LOANS_STATUS_NAME_MAP[status];
      details.color = 'gray';
      break;
    case 'EXPIRED':
      details.title = LOANS_STATUS_NAME_MAP[status];
      details.color = 'orange';
      break;
    case 'REJECTED':
      details.title = LOANS_STATUS_NAME_MAP[status];
      details.color = 'red';
      break;
    default:
      break;
  }
  return <StatusBadge title={details.title} color={details.color} />;
};

const getIssueBadge = (status: string, isIssuer?: boolean) => {
  const details: { title: string; color?: TokenStatusBadgeVariant } = {
    title: '',
  };
  switch (status) {
    case 'ISSUER_DOC_SIGNED':
      details.title = 'Требуется подписание Ключом транзакции инициализации';
      details.color = 'yellow';
      break;
    case 'RECIPIENT_DOC_SIGNED':
      details.title = 'Ожидается подпись Ключом Приобретателя';
      details.color = isIssuer ? 'grey' : 'yellow';
      break;
    case 'ISSUER_SIGNED':
      details.title = isIssuer
        ? 'Отправлено на согласование Приобретателю'
        : 'Подпишите согласие на приобретение';
      details.color = isIssuer ? 'grey' : 'yellow';
      break;
    case 'RECIPIENT_SIGNED':
      details.title = isIssuer
        ? 'Ожидается подтверждение оплаты'
        : 'Требуется подтвердить оплату';
      details.color = isIssuer ? 'grey' : 'yellow';
      break;
    case 'PAYMENT_DOCUMENT_UPLOADED':
      details.title = isIssuer
        ? 'Ожидается подтверждение оплаты и признание выпуска состоявшимся'
        : 'Ожидается подтверждение эмитентом';
      details.color = isIssuer ? 'yellow' : 'grey';
      break;
    case 'ISSUE_ACCEPT_SIGNED':
      details.title = isIssuer
        ? 'Требуется подписание Ключом'
        : 'Ожидается подтверждение эмитентом';
      details.color = isIssuer ? 'yellow' : 'grey';
      break;
    case 'CLOSED':
      details.title = 'Завершено';
      details.color = 'green';
      break;
    case 'COMPLETED':
      details.title = 'Завершено';
      details.color = 'green';
      break;
    default:
      break;
  }

  return <TokenStatusBadge text={details.title} variant={details.color} />;
};

const getTransactionsHistoryBadge = (
  status: string,
  type: TRANSACTION_TYPE,
  isInitiator: boolean
) => {
  console.log('getTransactionsHistoryBadge');

  const details: { title: string; color?: TokenStatusBadgeVariant } = {
    title: '',
  };
  switch (status) {
    case 'CONFIRMED':
      switch (type) {
        case 'CREDIT_REPAYMENT':
          details.title = 'Погашен';
          details.color = 'grey';
          break;
        case 'CREDIT':
          details.title = 'Выдан';
          details.color = 'green';
          break;
        case 'ISSUE_PAYMENT':
          details.title = 'Подтверждено';
          details.color = 'green';
          break;
        case 'PURCHASE':
          details.title = 'Подтверждена';
          details.color = 'green';
          break;
        case 'SALE':
          details.title = 'Подтверждена';
          details.color = 'green';
          break;
        default:
          details.title = 'Подтвержден';
          details.color = 'green';
      }
      break;
    case 'INIT':
      details.title = 'На подписании';
      details.color = 'yellow';
      break;
    case 'AGENT_SIGNED':
      details.title = 'Операция ожидает оплаты';
      details.color = 'yellow';
      break;
    case 'PAYMENT_DOC_UPLOADED':
      switch (type) {
        case 'PURCHASE':
          details.title = 'Ожидается проверка платежа продавцом';
          details.color = 'yellow';
          break;
        case 'SALE':
          details.title = 'Ожидается проверка платежа продавцом';
          details.color = 'yellow';
          break;
      }
      break;
    case 'PENDING':
      switch (type) {
        case 'SALE':
          details.title = 'Ожидает оплаты';
          details.color = 'yellow';
          break;
        case 'ISSUE_PAYMENT':
          details.title = 'Подтверждение';
          details.color = isInitiator ? 'grey' : 'yellow';
          break;
        case 'ISSUE':
          details.title = 'Подтверждение';
          details.color = 'yellow';
          break;
        default:
          details.title = 'Согласование';
          details.color = isInitiator ? 'yellow' : 'grey';
          break;
      }
      break;
    case 'REJECTED':
      details.title = 'Отклонена';
      details.color = 'red';
      break;
    case 'APPLIED':
      switch (type) {
        case 'CREDIT_REPAYMENT':
          details.title = 'Погашен';
          details.color = 'grey';
          break;
        case 'CREDIT':
          details.title = 'Выдан';
          details.color = 'green';
          break;
        case 'ISSUE_PAYMENT':
          details.title = 'Согласован';
          details.color = 'yellow';
          break;
        default:
          details.title = 'Заявка исполнена';
          details.color = 'grey';
      }
      break;
    case 'DOC_SIGNED':
      switch (type) {
        case 'SALE':
          details.title = 'Ожидает подписания Ключом продавца';
          details.color = 'yellow';
          break;
        default:
          details.title = 'Ожидает подтверждения';
          details.color = 'yellow';
          break;
      }
      break;
    case 'AGENT_DOC_SIGNED':
      switch (type) {
        case 'SALE':
          details.title = 'Ожидается загрузка документа об оплате';
          details.color = 'yellow';
          break;
        case 'PURCHASE':
          details.title = 'Ожидается подписание Ключом продавца';
          details.color = 'yellow';
          break;
        default:
          details.title = 'Ожидает подтверждения';
          details.color = 'yellow';
          break;
      }
      break;
    case 'SIGNED':
      switch (type) {
        case 'SALE':
          details.title = 'Ожидает подтверждения';
          details.color = 'yellow';
          break;
        default:
          details.title = LOANS_STATUS_NAME_MAP[status];
          details.color = 'yellow';
          break;
      }
      break;
    case 'CLOSED':
      details.title = LOANS_STATUS_NAME_MAP[status];
      details.color = 'grey';
      break;
    default:
      break;
  }

  return (
    <TokenStatusBadge
      text={details.title}
      variant={details.color}
      minWidth={160}
    />
  );
};

const getUserStatusBadge = (status: USERS_ACTION_TYPE) => {
  const details: { title: string; color?: STATUS_COLOR_TYPE } = { title: '' };
  switch (status) {
    case 'ACTIVE':
      details.color = 'green';
      details.title = 'Активный';
      break;
    case 'BLOCKED':
      details.color = 'orange';
      details.title = 'Заблокирован';
      break;
    case 'NEW':
      details.color = 'gray';
      details.title = 'Регистрация';
      break;
    default:
      break;
  }
  return <StatusBadge title={details.title} color={details.color} />;
};

export {
  getOrderDetailedStatusBadge,
  getOrderStatusBadge,
  getAssetTokenStatusBadge,
  getTransactionsHistoryBadge,
  getIssueBadge,
  getUserStatusBadge,
};
